import { FormattedMessage, FormattedNumber } from 'react-intl';
import Typography from '@alltrails/core/components/Typography';
import { getDiscountPercentage, getPriceTotal, isTrial } from '@alltrails/payment/utils/plan';
import type Coupon from '@alltrails/types/Coupon';
import type { Plan } from '@alltrails/types/Plan';
import styles from './PlanTypeCard.module.scss';

type Props = {
  plan: Plan;
  isActive?: boolean;
  isCouponApplied?: boolean;
  extraClasses?: string;
  coupon?: Coupon;
  billedAtOnce?: boolean;
};

// https://www.figma.com/file/gZaqAej8HzoFkyrjlQ4cNs/Integrating-Brand-Changes?node-id=586%3A30993
// https://www.figma.com/file/gZaqAej8HzoFkyrjlQ4cNs/Integrating-Brand-Changes?node-id=4500%3A91389
const PlanTypeCard = ({ billedAtOnce, plan, isActive = false, isCouponApplied = false, coupon, extraClasses }: Props) => {
  let { priceMonth, priceTotal, showDiscount } = plan;
  let discountAmount;
  const discountPercentage = getDiscountPercentage(plan, coupon);

  if (isCouponApplied && coupon && plan.allowCoupon && coupon.type !== 'FreeTrialCoupon') {
    showDiscount = true;
    discountAmount = (
      <FormattedMessage
        defaultMessage="{percent} off"
        values={{
          percent: <FormattedNumber style="percent" value={discountPercentage / 100} />
        }}
      />
    );
    priceTotal = getPriceTotal(plan, coupon);
    priceMonth = priceTotal / 12;
  }

  function getPlanDetails() {
    if (billedAtOnce) {
      return {
        billedAt: (
          <FormattedMessage
            defaultMessage="{perMonth}/month - billed once at {total}"
            values={{
              perMonth: <FormattedNumber style="currency" currency={plan.currency} value={priceMonth} />,
              total: <FormattedNumber style="currency" currency={plan.currency} value={priceTotal} />
            }}
          />
        ),
        label: <FormattedMessage defaultMessage="Annual plan" />
      };
    }

    if (isTrial(plan, coupon)) {
      return {
        billedAt: (
          <FormattedMessage
            defaultMessage="Then {perMonth}/month - billed annually at {total}. Pay {trialCost} today."
            values={{
              perMonth: <FormattedNumber style="currency" currency={plan.currency} value={priceMonth} />,
              total: <FormattedNumber style="currency" currency={plan.currency} value={priceTotal} />,
              trialCost: <FormattedNumber style="currency" currency={plan.currency} value={0} minimumFractionDigits={0} maximumFractionDigits={0} />
            }}
          />
        ),
        label: <FormattedMessage defaultMessage="First 7 days free" />
      };
    }

    return {
      billedAt: (
        <FormattedMessage
          defaultMessage="{perMonth}/month - billed annually at {total}"
          values={{
            perMonth: <FormattedNumber style="currency" currency={plan.currency} value={priceMonth} />,
            total: <FormattedNumber style="currency" currency={plan.currency} value={priceTotal} />
          }}
        />
      ),
      label: <FormattedMessage defaultMessage="Annual plan" />
    };
  }

  const planDetail = getPlanDetails();

  if (!planDetail) {
    throw new Error(`No plan detail found for recurly plan ${plan.recurlyPlanCode}`);
  }

  let discount;
  let billedAtLang = planDetail.billedAt;

  if (showDiscount) {
    // billing language is slightly different when discounting first year
    discount = <div className={styles.discount}>{discountAmount}</div>;

    if (
      [
        'alltrails_pro_annual_10',
        'alltrails_pro_annual_20',
        'alltrails_pro_annual_21',
        'alltrails_pro_annual_24',
        'alltrails_pro_annual_30',
        'alltrails_pro_annual_36'
      ].includes(plan.recurlyPlanCode)
    ) {
      billedAtLang = (
        <FormattedMessage
          defaultMessage="{perMonth}/month - 1st year billed at {total}"
          values={{
            perMonth: <FormattedNumber style="currency" currency={plan.currency} value={priceMonth} />,
            total: <FormattedNumber style="currency" currency={plan.currency} value={priceTotal} />
          }}
        />
      );
    }
  }

  return (
    <div className={`${isActive ? styles.active : ''} ${styles.button} ${styles.card} ${extraClasses}`}>
      <div className={styles.contents}>
        <div className={styles.label}>
          <Typography component="div" variant="heading200">
            {planDetail.label}
          </Typography>
          {discount}
        </div>
        <Typography component="div" variant="text200">
          {billedAtLang}
        </Typography>
      </div>
    </div>
  );
};

export default PlanTypeCard;
